import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Lock, Trash2 } from 'lucide-react';

import { useSyncOuEnLigne } from '../../context/SyncContext';
import { lotPaymentsAPI, urlImpressionDecompte } from '../../lib/lotPaymentsApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate, formatPrixUnitaire, symboleDevise } from '../../lib/formats';
import {
  corpsDecompte,
  lignesDecompte,
  ligneDecompteAffichee,
  saisieDecompte,
} from '../../lib/lotSettlements';
import {
  calculerTotaux,
  creerGardeCle,
  montantAvanceMax,
  nombreSaisi,
  nouvelleCle,
  totauxDepuisServeur,
} from '../../lib/transportStatements';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { useDroitsLots } from '../../hooks/useDroitsLots';
import { PrintMenu } from '../../components/business/PrintMenu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { EmptyState } from '../../components/ui/empty-state';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LoadingState } from '../../components/ui/loading-state';
import { PageHeader } from '../../components/ui/page-header';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../components/ui/sonner';
import { AcomptesDecompte, RecapDecompte, ReglementsDecompte } from '../../components/lots/DecompteAcomptes';
import { CamionsDecompte, LignesDecompte } from '../../components/lots/DecompteCamions';
import { PaiementDecompteDialog } from '../../components/lots/PaiementDecompteDialog';
import { StatutDecompte, StatutPaiement, statMontant } from '../../components/lots/paiementsUi';

const CADRE = 'mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8';

/**
 * Un décompte du lot : le règlement du partenaire camion par camion. En
 * brouillon, on coche des camions dépotés, on vérifie le prix de retenue, on
 * saisit le dépoté manquant et on coche les acomptes ; une fois validé (numéro
 * et facture attribués), le décompte est figé.
 *
 * Les montants affichés pendant la saisie sont un aperçu ; dès l'enregistrement
 * ce sont ceux du serveur. Toute écriture porte une clé d'idempotence.
 */
const LotDecomptePage = () => {
  const { id, sid } = useParams();
  const nouveau = !sid;
  const navigate = useNavigate();
  const { peutEcrire, voitArgent } = useDroitsLots();
  // L'argent attend le serveur : boutons inactifs sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;

  const [etat, setEtat] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [saisie, setSaisie] = useState(null);
  const [failed, setFailed] = useState(false);
  const [modifie, setModifie] = useState(false);
  const [busy, setBusy] = useState('');
  const [dialogue, setDialogue] = useState(null); // 'valider' | 'annuler' | 'supprimer' | 'payer'
  const [motif, setMotif] = useState('');

  const modifieRef = useRef(false);
  const gardeEnregistrement = useRef(creerGardeCle('decompte-enregistrer'));
  const gardeAnnulation = useRef(creerGardeCle('decompte-annuler'));
  const cleValidation = useRef(nouvelleCle('decompte-valider'));

  const retourPaiements = `/lots/${id}/paiements`;

  const charger = useCallback(
    async ({ silencieux = false, forcer = false } = {}) => {
      if (!silencieux) setFailed(false);
      try {
        const detail = nouveau ? null : await lotPaymentsAPI.getSettlement(id, sid);
        const brouillon = nouveau || detail?.status === 'BROUILLON';
        const choix = brouillon ? await lotPaymentsAPI.eligible(id, nouveau ? undefined : sid) : null;
        setEtat(detail);
        setEligible(choix);
        setFailed(false);
        if (forcer || !modifieRef.current) {
          setSaisie(brouillon ? saisieDecompte(detail, choix) : null);
          modifieRef.current = false;
          setModifie(false);
        }
      } catch (error) {
        if (!silencieux) {
          setFailed(true);
          toast.error(messageErreur(error, 'Impossible de charger ce décompte'));
        }
      }
    },
    [id, sid, nouveau],
  );

  useEffect(() => {
    if (voitArgent) charger({ forcer: true });
  }, [charger, voitArgent]);

  useRafraichirSur(['lots', 'finance', 'trajets'], charger, { actif: voitArgent });

  const lecture = Boolean(etat && etat.status !== 'BROUILLON');
  const lot = etat?.lot || eligible?.lot || null;
  const devise = etat?.currency || eligible?.defaults?.currency || lot?.currency || 'XOF';
  const prixLot = lot?.price_per_liter ?? eligible?.defaults?.price ?? null;

  const lignesBrutes = useMemo(() => lignesDecompte(eligible, etat), [eligible, etat]);

  const affichees = useMemo(() => {
    if (!saisie) return [];
    const enregistrees = new Map((etat?.lines || []).map((ligne) => [ligne.lot_truck_id, ligne]));
    return lignesBrutes.map((ligne) =>
      ligneDecompteAffichee(ligne, saisie, devise, prixLot, !modifie ? enregistrees.get(ligne.lot_truck_id) || null : null),
    );
  }, [lignesBrutes, saisie, devise, prixLot, etat, modifie]);

  const totauxLocaux = useMemo(() => {
    if (!saisie) return calculerTotaux([], {}, devise);
    const choisies = affichees.filter((ligne) => saisie.selectionnes.has(ligne.lot_truck_id));
    const avances = (eligible?.advances || []).reduce(
      (somme, acompte) => somme + (nombreSaisi(saisie.avances[acompte.payment_id]) || 0),
      0,
    );
    return calculerTotaux(choisies, { avances }, devise);
  }, [affichees, saisie, eligible, devise]);

  // Un brouillon propre montre les totaux du serveur ; en cours de saisie, l'aperçu.
  const totaux = useMemo(() => {
    if (lecture || (!modifie && etat?.totals)) return totauxDepuisServeur(etat?.totals);
    return totauxLocaux;
  }, [lecture, modifie, etat, totauxLocaux]);

  const modifierSaisie = useCallback((changer) => {
    setSaisie((precedente) => (precedente ? changer(precedente) : precedente));
    modifieRef.current = true;
    setModifie(true);
  }, []);

  const changerChamp = (nom, valeur) => modifierSaisie((s) => ({ ...s, [nom]: valeur }));

  const basculer = (ligneId) =>
    modifierSaisie((s) => {
      const suivants = new Set(s.selectionnes);
      if (suivants.has(ligneId)) suivants.delete(ligneId);
      else suivants.add(ligneId);
      return { ...s, selectionnes: suivants };
    });

  const basculerTous = (ligneIds, coche) =>
    modifierSaisie((s) => {
      const suivants = new Set(s.selectionnes);
      ligneIds.forEach((ligneId) => (coche ? suivants.add(ligneId) : suivants.delete(ligneId)));
      return { ...s, selectionnes: suivants };
    });

  const changerLivre = (ligneId, valeur) =>
    modifierSaisie((s) => ({ ...s, livre: { ...s.livre, [ligneId]: valeur } }));

  /** Prix, montant ou retenue saisi à la main ; un texte vide revient au calcul. */
  const changerManuel = (ligneId, cle, valeur) =>
    modifierSaisie((s) => {
      const { [cle]: _ancien, ...autres } = s.manuels?.[ligneId] || {};
      const ligne = valeur === '' ? autres : { ...autres, [cle]: valeur };
      const { [ligneId]: _retire, ...reste } = s.manuels || {};
      return { ...s, manuels: Object.keys(ligne).length > 0 ? { ...reste, [ligneId]: ligne } : reste };
    });

  const basculerAcompte = (acompte) => {
    if (saisie?.avances[acompte.payment_id] !== undefined) {
      modifierSaisie((s) => {
        const { [acompte.payment_id]: _retire, ...reste } = s.avances;
        return { ...s, avances: reste };
      });
      return;
    }
    const montant = montantAvanceMax(acompte, totauxLocaux.net);
    if (montant <= 0) {
      toast.info('Il ne reste rien à payer sur ce décompte');
      return;
    }
    modifierSaisie((s) => ({ ...s, avances: { ...s.avances, [acompte.payment_id]: String(montant) } }));
  };

  const changerMontantAcompte = (acompte, valeur) => {
    const nombre = nombreSaisi(valeur);
    const borne = nombre !== null && nombre > Number(acompte.available) ? String(acompte.available) : valeur;
    modifierSaisie((s) => ({ ...s, avances: { ...s.avances, [acompte.payment_id]: borne } }));
  };

  /** Enregistre le brouillon (création ou modification) ; rend le décompte enregistré, ou `null`. */
  const sauvegarder = async () => {
    const corps = corpsDecompte({ lignes: lignesBrutes, saisie, avances: eligible?.advances || [] });
    if (corps.line_ids.length === 0) {
      toast.error('Choisissez au moins un camion dépoté');
      return null;
    }
    const cle = gardeEnregistrement.current({ sid: sid || null, corps });
    try {
      const resultat = nouveau
        ? await lotPaymentsAPI.createSettlement(id, corps, cle)
        : await lotPaymentsAPI.updateSettlement(id, sid, corps, cle);
      toast.success('Décompte enregistré');
      modifieRef.current = false;
      setModifie(false);
      if (nouveau && resultat?.id) {
        navigate(`/lots/${id}/decomptes/${resultat.id}`, { replace: true });
      } else {
        await charger({ silencieux: true, forcer: true });
      }
      return resultat || null;
    } catch (error) {
      toast.error(messageErreur(error, 'Enregistrement du décompte impossible'));
      return null;
    }
  };

  const enregistrer = async () => {
    setBusy('enregistrer');
    try {
      await sauvegarder();
    } finally {
      setBusy('');
    }
  };

  const valider = async () => {
    setBusy('valider');
    try {
      if (modifieRef.current && !(await sauvegarder())) return;
      const resultat = await lotPaymentsAPI.validateSettlement(id, sid, cleValidation.current);
      toast.success(resultat?.reference ? `Décompte ${resultat.reference} validé` : 'Décompte validé');
      await charger({ silencieux: true, forcer: true });
    } catch (error) {
      toast.error(messageErreur(error, 'Validation du décompte impossible'));
    } finally {
      setDialogue(null);
      setBusy('');
    }
  };

  const annuler = async () => {
    setBusy('annuler');
    try {
      const raison = motif.trim();
      const cle = gardeAnnulation.current({ sid, raison });
      await lotPaymentsAPI.cancelSettlement(id, sid, { idempotencyKey: cle, reason: raison || undefined });
      setDialogue(null);
      if (etat?.status === 'BROUILLON') {
        toast.success('Brouillon annulé');
        navigate(retourPaiements, { replace: true });
      } else {
        toast.success('Décompte annulé');
        await charger({ silencieux: true, forcer: true });
      }
    } catch (error) {
      setDialogue(null);
      toast.error(messageErreur(error, 'Annulation du décompte impossible'));
    } finally {
      setBusy('');
    }
  };

  const ouvrirAnnulation = () => {
    setMotif('');
    setDialogue('annuler');
  };

  /** Un décompte annulé se supprime ; on revient aux paiements du lot. */
  const supprimer = async () => {
    setBusy('supprimer');
    try {
      await lotPaymentsAPI.deleteSettlement(id, sid);
      setDialogue(null);
      toast.success('Décompte supprimé');
      navigate(retourPaiements, { replace: true });
    } catch (error) {
      setDialogue(null);
      toast.error(messageErreur(error, 'Suppression du décompte impossible'));
    } finally {
      setBusy('');
    }
  };

  if (!voitArgent) {
    return (
      <div className={CADRE} data-testid="lot-decompte-page">
        <PageHeader eyebrow="Lots attribués" title="Décompte" backTo={retourPaiements} backLabel="Paiements du lot" />
        <EmptyState variant="error" icon={Lock} title="Ces montants sont réservés à la direction" />
      </div>
    );
  }

  // Les données affichées doivent être celles de l'adresse : après l'enregistrement
  // d'un nouveau décompte, l'adresse change avant que le décompte enregistré soit relu.
  const donneesAJour = nouveau ? etat === null && eligible !== null : etat?.id === sid;

  if (!donneesAJour && !failed) {
    return (
      <div className={CADRE}>
        <LoadingState shape="detail" />
      </div>
    );
  }

  if (failed || !donneesAJour) {
    return (
      <div className={CADRE}>
        <PageHeader eyebrow="Lots attribués" title="Décompte" backTo={retourPaiements} backLabel="Paiements du lot" />
        <EmptyState
          variant="error"
          title="Ce décompte n’a pas pu être chargé"
          actionLabel="Réessayer"
          onAction={() => charger({ forcer: true })}
        />
      </div>
    );
  }

  const statut = etat?.status || 'BROUILLON';
  const aEnregistrer = nouveau || modifie;
  const occupe = Boolean(busy);
  const ecritureBloquee = occupe || !enLigne || !peutEcrire;
  const nombreChoisis = lecture ? totaux.lignes : (saisie?.selectionnes.size ?? 0);
  const avertissements = !modifie && !lecture ? etat?.warnings || [] : [];
  const reference = etat?.reference || null;
  const prixAffiche = prixLot === null || prixLot === undefined ? null : formatPrixUnitaire(prixLot, devise);
  // Le reste d'un décompte validé se paie depuis ici : sa facture (pas un avoir) a encore un reste dû.
  const facture = lecture && statut === 'VALIDE' ? etat.bill : null;
  const resteAPayer =
    facture && facture.kind !== 'SUPPLIER_CREDIT' && facture.active !== false ? Math.max(0, Number(facture.remaining) || 0) : 0;

  return (
    <div className={CADRE} data-testid="lot-decompte-page">
      <PageHeader
        eyebrow={`Lots attribués · ${lot?.reference || 'Lot'}${lot?.transporter_name ? ` · ${lot.transporter_name}` : ''}`}
        title={reference || (nouveau ? 'Nouveau décompte' : 'Décompte')}
        backTo={retourPaiements}
        backLabel="Paiements du lot"
        stats={[
          { label: 'Camions', valeur: nombreChoisis, testid: 'decompte-stat-camions' },
          statMontant({
            label: 'Net à payer',
            valeur: totaux.net,
            devise,
            ton: 'positif',
            testid: 'decompte-net',
          }),
        ]}
        meta={
          <>
            {nouveau ? <Badge variant="warning">Brouillon</Badge> : <StatutDecompte code={statut} data-testid="decompte-statut" />}
            {lecture && statut === 'VALIDE' && etat.bill?.payment_status ? (
              <StatutPaiement code={etat.bill.payment_status} />
            ) : null}
            {!lecture && aEnregistrer && !nouveau ? <Badge variant="info">Non enregistré</Badge> : null}
            {lecture ? <span>Établi le {formatDate(etat.issued_at)}</span> : null}
            {prixAffiche ? <span className="tabular-nums">Lot {prixAffiche}/L</span> : null}
            {lecture && etat.retention_price !== null && etat.retention_price !== undefined ? (
              <span className="tabular-nums">Retenue {formatPrixUnitaire(etat.retention_price, devise)}/L</span>
            ) : null}
            {statut === 'ANNULE' && etat.cancel_reason ? <span>Motif : {etat.cancel_reason}</span> : null}
            {avertissements.length > 0 ? (
              <Aide libelle={`${avertissements.length} à revoir`} titre="À revoir" ton="attention" points={avertissements} />
            ) : null}
          </>
        }
        actions={
          <>
            {resteAPayer > 0 && peutEcrire ? (
              <Button onClick={() => setDialogue('payer')} disabled={ecritureBloquee} data-testid="decompte-payer">
                Payer le reste
              </Button>
            ) : null}
            {!lecture && peutEcrire ? (
              <Button
                variant={aEnregistrer ? 'default' : 'outline'}
                onClick={enregistrer}
                disabled={ecritureBloquee || !aEnregistrer}
                data-testid="decompte-enregistrer"
              >
                {busy === 'enregistrer' ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            ) : null}
            {!lecture && !nouveau && peutEcrire ? (
              <Button
                variant={aEnregistrer ? 'outline' : 'default'}
                onClick={() => setDialogue('valider')}
                disabled={ecritureBloquee}
                data-testid="decompte-valider"
              >
                Valider
              </Button>
            ) : null}
            {!nouveau && statut !== 'ANNULE' && peutEcrire ? (
              <Button variant="outline" onClick={ouvrirAnnulation} disabled={ecritureBloquee} data-testid="decompte-annuler">
                Annuler le décompte
              </Button>
            ) : null}
            {!nouveau && statut === 'ANNULE' && peutEcrire ? (
              <Button
                variant="outline"
                onClick={() => setDialogue('supprimer')}
                disabled={ecritureBloquee}
                className="text-destructive hover:border-destructive/40 hover:text-destructive"
                data-testid="decompte-supprimer"
              >
                <Trash2 aria-hidden="true" />
                Supprimer
              </Button>
            ) : null}
            {!nouveau ? (
              <PrintMenu
                label="Imprimer"
                items={[
                  {
                    label: reference || 'Brouillon',
                    url: urlImpressionDecompte(id, sid),
                    filename: `decompte-${reference || 'brouillon'}`,
                    formats: ['pdf', 'xlsx'],
                  },
                ]}
              />
            ) : null}
          </>
        }
      />

      {!lecture && saisie ? (
        <>
          <div className="flex flex-wrap items-end gap-4" data-testid="decompte-entete-saisie">
            <div className="space-y-1">
              <Label htmlFor="decompte-emis-le">Établi le</Label>
              <Input
                id="decompte-emis-le"
                type="date"
                value={saisie.emisLe}
                disabled={ecritureBloquee}
                onChange={(evenement) => changerChamp('emisLe', evenement.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="decompte-prix-retenue">Prix de retenue</Label>
              <div className="relative">
                <Input
                  id="decompte-prix-retenue"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  value={saisie.prixRetenue}
                  disabled={ecritureBloquee}
                  onChange={(evenement) => changerChamp('prixRetenue', evenement.target.value)}
                  className="w-44 pr-16 text-right tabular-nums"
                  data-testid="decompte-prix-retenue"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {symboleDevise(devise)}/L
                </span>
              </div>
            </div>
            <div className="min-w-[12rem] flex-1 space-y-1">
              <Label htmlFor="decompte-observations">Observations</Label>
              <Input
                id="decompte-observations"
                value={saisie.notes}
                maxLength={1000}
                disabled={ecritureBloquee}
                onChange={(evenement) => changerChamp('notes', evenement.target.value)}
                data-testid="decompte-observations"
              />
            </div>
          </div>

          <CamionsDecompte
            lignes={affichees}
            saisie={saisie}
            totaux={totaux}
            devise={devise}
            disabled={ecritureBloquee}
            onBasculer={basculer}
            onBasculerTous={basculerTous}
            onLivre={changerLivre}
            onManuel={changerManuel}
          />
          <AcomptesDecompte
            acomptes={eligible?.advances || []}
            saisie={saisie}
            devise={devise}
            disabled={ecritureBloquee}
            onBasculer={basculerAcompte}
            onMontant={changerMontantAcompte}
          />
        </>
      ) : null}

      {lecture ? (
        <>
          <LignesDecompte lignes={etat.lines || []} totaux={totaux} devise={devise} />
          <AcomptesDecompte acomptes={etat.advances || []} saisie={{ avances: {} }} devise={devise} lecture />
          <ReglementsDecompte reglements={etat.payments || []} devise={devise} />
          {etat.notes ? (
            <p className="text-sm text-muted-foreground" data-testid="decompte-notes">
              {etat.notes}
            </p>
          ) : null}
        </>
      ) : null}

      <RecapDecompte
        totaux={totaux}
        bill={lecture && statut === 'VALIDE' ? etat.bill : null}
        devise={devise}
        apercu={modifie}
      />

      {resteAPayer > 0 && peutEcrire ? (
        <PaiementDecompteDialog
          open={dialogue === 'payer'}
          onOpenChange={(ouvert) => setDialogue(ouvert ? 'payer' : null)}
          lotId={id}
          settlementId={sid}
          reference={reference}
          reste={resteAPayer}
          devise={devise}
          onDone={() => charger({ silencieux: true, forcer: true })}
        />
      ) : null}

      <AlertDialog open={dialogue === 'valider'} onOpenChange={(ouvert) => !ouvert && !busy && setDialogue(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Valider ce décompte ?</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">
              Le numéro et la facture du partenaire sont attribués ; le décompte ne se modifie plus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'valider'}>Pas maintenant</AlertDialogCancel>
            <AlertDialogAction
              onClick={(evenement) => {
                evenement.preventDefault();
                valider();
              }}
              disabled={busy === 'valider' || !enLigne}
              data-testid="decompte-confirmer-validation"
            >
              {busy === 'valider' ? 'Validation…' : 'Valider le décompte'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dialogue === 'annuler'} onOpenChange={(ouvert) => !ouvert && !busy && setDialogue(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{statut === 'VALIDE' ? 'Annuler ce décompte ?' : 'Annuler ce brouillon ?'}</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">
              Les camions redeviennent disponibles pour un autre décompte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {statut === 'VALIDE' ? (
            <div className="space-y-1.5">
              <Label htmlFor="decompte-motif-annulation">Motif</Label>
              <Textarea
                id="decompte-motif-annulation"
                rows={2}
                value={motif}
                onChange={(evenement) => setMotif(evenement.target.value)}
                data-testid="decompte-motif-annulation"
              />
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'annuler'}>Garder le décompte</AlertDialogCancel>
            <AlertDialogAction
              onClick={(evenement) => {
                evenement.preventDefault();
                annuler();
              }}
              disabled={busy === 'annuler' || !enLigne || (statut === 'VALIDE' && !motif.trim())}
              data-testid="decompte-confirmer-annulation"
            >
              {busy === 'annuler' ? 'Annulation…' : 'Annuler le décompte'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dialogue === 'supprimer'} onOpenChange={(ouvert) => !ouvert && !busy && setDialogue(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce décompte annulé ?</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">
              Le décompte disparaît des listes du lot ; son numéro n’est pas repris.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'supprimer'}>Garder le décompte</AlertDialogCancel>
            <AlertDialogAction
              onClick={(evenement) => {
                evenement.preventDefault();
                supprimer();
              }}
              disabled={busy === 'supprimer' || !enLigne}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="decompte-confirmer-suppression"
            >
              {busy === 'supprimer' ? 'Suppression…' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LotDecomptePage;
